/**
 * NearMeChip — compact dropdown trigger that lives inline with the
 * country filter (no pill row of its own).
 *
 *   ┌──────────────────┐  ┌────────────────────┐
 *   │ 🇬🇧 United… ▾ │  │ 📍 Near me · 25mi ▾ │
 *   └──────────────────┘  └────────────────────┘
 *
 * Tap → dropdown:
 *   From anywhere   (✓ when chip is off)
 *   ────────────────
 *   Within  5 miles
 *   Within 10 miles
 *   Within 15 miles
 *   Within 20 miles
 *   Within 25 miles   ← (✓ on the active one)
 *   Within 30 miles
 *   ...
 */
import { useEffect, useState } from 'react';
import { MapPin, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import useNearby from '@/context/NearbyContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

// 5-mile increments — 5 mi (tight urban) up to 50 mi (regional day-trip).
const PRESETS = [5, 10, 15, 20, 25, 30, 40, 50];

export default function NearMeChip({ autoPrompt = false }) {
  const {
    enabled, radiusMi, status,
    setRadiusMi, enable, disable, ensurePromptOnce,
  } = useNearby();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!autoPrompt) return;
    ensurePromptOnce();
  }, [autoPrompt, ensurePromptOnce]);

  const handlePick = async (mi) => {
    if (busy) return;
    setBusy(true);
    try {
      if (!enabled) {
        const ok = await enable();
        if (!ok) {
          if (status === 'unavailable') {
            toast.error('Location not available on this device');
          } else {
            toast('Location blocked — turn on location access to filter by distance');
          }
          return;
        }
      }
      setRadiusMi(mi);
    } finally {
      setBusy(false);
    }
  };

  // Active label kept tight ("📍 25 mi ▾") so it fits beside the country
  // dropdown on a 390px screen. When the chip is OFF we show "Anywhere" —
  // the events list is global by default, so "Near me" was a confusing
  // misnomer (it described the *future* state, not the current one).
  const label = enabled ? `${radiusMi} mi` : 'Anywhere';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/40 hover:bg-card/70 hover:border-border transition px-4 py-2 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid="near-me-trigger"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <MapPin className={`w-4 h-4 ${enabled ? 'text-cyan-400' : 'text-muted-foreground'}`} />
        )}
        <span>{label}</span>
        <span aria-hidden="true" className="text-muted-foreground text-xs">▾</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="w-[200px] max-h-[320px] overflow-y-auto"
        data-testid="near-me-menu"
      >
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
          Show events…
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => disable()}
          data-testid="near-me-option-off"
          className="cursor-pointer"
        >
          <span className="flex-1">From anywhere</span>
          {!enabled && <Check className="w-4 h-4 text-cyan-400" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {PRESETS.map((mi) => (
          <DropdownMenuItem
            key={mi}
            onClick={() => handlePick(mi)}
            data-testid={`near-me-option-${mi}`}
            className="cursor-pointer"
          >
            <span className="flex-1">Within {mi} miles</span>
            {enabled && radiusMi === mi && <Check className="w-4 h-4 text-cyan-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
